/**
 * 各種機器管理 API（/api/equipment/*）。担当: G1
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface EquipmentDto {
  id: number;
  fiscalYear: number | null;
  businessOperatorId: number;
  businessOperatorName?: string;
  managementNo: number;
  category: string;
  vesselId: number | null;
  vesselName: string | null;
  registrationNo: string | null;
  tonnage: number | null;
  nonParticipantVesselName: string | null;
  ownerName: string | null;
  userName: string | null;
  identificationNo: string | null;
  maker: string | null;
  modelNo: string | null;
  subsidyProgramName: string | null;
  totalCost: number | null;
  subsidyAmount: number | null;
  operatorShare: number | null;
  usefulLifeYears: number | null;
  disposalRestrictedUntil: string | null;
  disposalApprovedOn: string | null;
  disposalDetail: string | null;
  licensedOn: string | null;
  licenseExpiresOn: string | null;
  lendingFrom: string | null;
  lendingTo: string | null;
  note: string | null;
  isDuplicate?: boolean;
}

export interface EquipmentList {
  items: EquipmentDto[];
  total: number;
  duplicates: { identificationNos: string[]; registrationNos: string[] };
}

export interface EquipmentFilter {
  businessOperatorId?: string;
  category?: string;
  fiscalYear?: string;
  identificationNo?: string;
  vesselName?: string;
}

export const equipmentKeys = {
  all: ['equipment'] as const,
  list: (f: EquipmentFilter) => [...equipmentKeys.all, 'list', f] as const,
  detail: (id: number) => [...equipmentKeys.all, 'detail', id] as const,
};

export function useEquipmentList(filter: EquipmentFilter) {
  return useQuery({
    queryKey: equipmentKeys.list(filter),
    queryFn: ({ signal }) => api.get<EquipmentList>('/equipment', { ...filter, pageSize: 500 }, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useEquipment(id: number | null) {
  return useQuery({
    queryKey: equipmentKeys.detail(id ?? 0),
    queryFn: ({ signal }) => api.get<EquipmentDto>(`/equipment/${id}`, undefined, { signal }),
    enabled: id != null,
  });
}

export function useSaveEquipment(id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => (id == null ? api.post<EquipmentDto>('/equipment', body) : api.put<EquipmentDto>(`/equipment/${id}`, body)),
    onSuccess: () => qc.invalidateQueries({ queryKey: equipmentKeys.all }),
  });
}

export function useDeleteEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/equipment/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: equipmentKeys.all }),
  });
}
